"use client";

import React from "react";
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Portal,
  VStack,
} from "@chakra-ui/react";

export type GenericConfirmationDialogProps = {
  isOpen: boolean;
  onCloseAction: () => void;
  onConfirmAction: () => void | Promise<void>;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  children?: React.ReactNode;
};

function GenericConfirmationDialogInner({
  isOpen,
  onCloseAction,
  onConfirmAction,
  // Textual customization
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmDisabled = false,
  children,
}: GenericConfirmationDialogProps) {
  const handleConfirm = async () => {
    if (confirmDisabled) {
      return;
    }

    await onConfirmAction();
    onCloseAction();
  };

  const handleKeyDown = async (event: React.KeyboardEvent) => {
    if (
      event.key === "Enter" &&
      (event.ctrlKey || event.metaKey)
    ) {
      event.preventDefault();
      await handleConfirm();
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => !open && onCloseAction()}
      lazyMount={true}
      restoreFocus={false}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg="moveon.surface.bg"
            color="moveon.surface.fg"
            borderColor="moveon.surface.border"
            onKeyDown={handleKeyDown}
          >
            <Dialog.Header>
              <Dialog.Title>{title}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch">
                <Box>{description}</Box>
                {children && <Box>{children}</Box>}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline" onClick={onCloseAction}>
                  {cancelText}
                </Button>
              </Dialog.ActionTrigger>
              <Button
                variant="surface"
                colorPalette="red"
                onClick={handleConfirm}
                disabled={confirmDisabled}
              >
                {confirmText}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" onClick={onCloseAction} />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

const GenericConfirmationDialog = React.memo(GenericConfirmationDialogInner);
export default GenericConfirmationDialog;
